import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiUnlock, FiMap, FiClock, FiCheck, FiArrowRight} = FiIcons;

const CurriculumSection = () => {
  const days = [
    {
      day: "วันแรก",
      title: "ถอดรหัส Bazi Blueprint ของคุณ",
      subtitle: "เรียนรู้แก่นแท้แห่งตัวตน หรือ ดิถี (日主,Day Master)",
      icon: FiUnlock,
      color: "from-cyan-500 to-blue-500",
      activities: [
        "เปิดประตูสู่โลกแห่งศาสตร์ปาจื้อ (八字,Bazi) ด้วยการสร้างผังส่วนตัว",
        "ถอดรหัสธาตุแท้และพลังงานหลักที่กำหนดตัวตนของคุณ",
        "เข้าใจระบบ 5 ธาตุ และการไหลเวียนของพลังงานในชีวิต"
      ]
    },
    {
      day: "วันที่สอง",
      title: "อ่านแผนที่พรสวรรค์ในผัง Bazi",
      subtitle: "เจาะลึกระบบสิบเทพ (十神,Shí Shén) เพื่อไขรหัสศักยภาพ",
      icon: FiMap,
      color: "from-purple-500 to-indigo-500",
      activities: [
        "ค้นพบ 'พรสวรรค์ที่ซ่อนเร้น' ผ่านระบบสิบเทพ (十神)",
        "วิเคราะห์จุดแข็ง จุดอ่อน และโอกาสเฉพาะตัว",
        "เรียนรู้การอ่านความสัมพันธ์และการทำงานร่วมกับผู้อื่น"
      ]
    },
    {
      day: "วันที่สาม",
      title: "บัญชาชีวิตด้วยจังหวะแห่ง Bazi",
      subtitle: "เข้าใจวัยจร (大運,Dà Yùn) และสร้างแผนปฏิบัติการ",
      icon: FiClock,
      color: "from-indigo-500 to-purple-500",
      activities: [
        "เรียนรู้ศิลปะการอ่าน 'วัยจร' (大運) สำหรับการวางแผนระยะยาว",
        "ประยุกต์ใช้พลังงานรายปี-รายเดือนในการตัดสินใจ",
        "สร้าง 'แผนปฏิบัติการ Bazi' ส่วนตัวสำหรับ 90 วันข้างหน้า"
      ]
    }
  ];

  return (
    <section className="py-20 relative" id="curriculum">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900"></div>
      
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          viewport={{once: true}}
        >
          <h2 className="text-4xl md:text-5xl font-light text-white mb-8">
            แก่นแห่งวิชาปาจื้อ (八字,Bazi) <br />
            <span className="text-indigo-400">ที่จะถูกถ่ายทอดใน 3 วัน</span>
          </h2>
        </motion.div>

        <div className="space-y-12">
          {days.map((day, index) => (
            <motion.div
              key={index}
              className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-lg rounded-3xl p-10 border border-white/10"
              initial={{opacity: 0, x: index % 2 === 0 ? -50 : 50}}
              whileInView={{opacity: 1, x: 0}}
              transition={{duration: 0.8, delay: index * 0.2}}
              viewport={{once: true}}
            >
              <div className="flex flex-col lg:flex-row items-start gap-10">
                {/* Day Header */}
                <div className="lg:w-1/3">
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${day.color} flex items-center justify-center`}>
                      <SafeIcon icon={day.icon} className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <div className="text-2xl font-light text-white">{day.day}</div>
                      <h3 className="text-xl font-light text-slate-200">{day.title}</h3>
                    </div>
                  </div>
                  <div className={`bg-gradient-to-r ${day.color}/20 rounded-2xl p-6 border border-white/10`}>
                    <p className="text-slate-300 font-light text-sm leading-relaxed">
                      {day.subtitle}
                    </p>
                  </div>
                </div>

                {/* Activities */}
                <div className="lg:w-2/3">
                  <div className="space-y-4">
                    {day.activities.map((activity, activityIndex) => (
                      <motion.div
                        key={activityIndex}
                        className="flex items-start space-x-4"
                        initial={{opacity: 0, y: 20}}
                        whileInView={{opacity: 1, y: 0}}
                        transition={{duration: 0.5, delay: (index * 0.2) + (activityIndex * 0.1)}}
                        viewport={{once: true}}
                      >
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-r ${day.color} flex items-center justify-center mt-1 flex-shrink-0`}>
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-slate-300 font-light leading-relaxed">{activity}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          className="text-center mt-16"
          initial={{opacity: 0, y: 30}}
          whileInView={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 0.5}}
          viewport={{once: true}}
        >
          <div className="bg-gradient-to-r from-indigo-900/30 to-purple-900/30 rounded-2xl p-8 border border-indigo-500/20 backdrop-blur-sm">
            <p className="text-xl text-indigo-100 font-light mb-6">
              3 วันที่จะเปลี่ยนวิธีคิดและการตัดสินใจของคุณไปตลอดกาล
            </p>
            <motion.a
              href="https://www.fengshuix.com/event-details/the-bazi-blueprint"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-10 py-4 rounded-2xl text-lg font-light shadow-2xl hover:shadow-indigo-500/25 transition-all duration-300 inline-flex items-center space-x-3"
              whileHover={{scale: 1.05, y: -2}}
              whileTap={{scale: 0.95}}
            >
              <span>เข้าสู่การเดินทางแห่งศาสตร์ Bazi</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CurriculumSection;